import { AdSlotType } from '../../../advertising/AdTypes';
import { WORLD_CONFIG } from '../../../config/world.config';
import { ObstacleType } from '../../../obstacles/ObstacleType';
import { DEG_TO_RAD } from '../../../utils/math';
import { mirroredPlacements } from '../hazardBuilders';
import { COLLECTIBLE_FLOAT_HEIGHT, collectibleLanes, entryConnector, laneArea, moduleSides, obstacleRow, standardLanes, straightExit } from '../segmentBuilders';
import {
  EnclosureKind,
  SegmentKind,
  type AdvertisingSlotDefinition,
  type CeilingZone,
  type DecorationSocket,
  type FixedObstaclePlacement,
  type ObstacleVariant,
  type SegmentDefinition,
} from '../SegmentDefinition';

/**
 * SEG_14 – basement / tunnel 40 m (design/01_segments/SEG_14_Tunnel_Basement). A concrete
 * tunnel under an earth berm over the whole module: dark, lit by lamps along the walls,
 * pipes and cable trays on the walls, puddles on the floor (visual only).
 *
 *   Roof        z 0–40, underside 4.2 m (soft: limits the double jump, never a crash)
 *   Walls       inner faces x = ±3.95 m (0.6 m thick, they hide the road module's fence)
 *   Low pipes   PIPE_A z 12 over all lanes, PIPE_B z 28 over lanes 1–2 – 1.3 m clearance (slide). Ø 0.6 m
 *               on thin hangers between the lanes, the roof 2.3 m above: their top (1.9 m) is walkable – a
 *               double jump under the roof clears them (SEGMENT_STANDARD "Sprungehrlichkeit")
 *   Rows        D1 z 5 (material stack: lane change), D2 z 20, D3 z 36 (cable drums: jump – only 8 m after
 *               the pipes, too close for a lane change at 18 m/s); six layouts (TUNNEL_LAYOUTS)
 *   Lighting    enclosure TUNNEL: daylight dimmed inside, the runner light and the wall lamps take over
 *   Ads         portal banner over the entry (AD_SCAFFOLD_HIGH), wall banners z 10 / z 30
 *
 * Road: two SEG_STRAIGHT_20M modules (asphalt, barriers); tunnel shell as greybox overlay
 * (assets/manifest/enclosureGreybox.ts) until the Blender module exists.
 */
export const TUNNEL = {
  length: 40,
  roof: { clearance: 4.2, thickness: 0.5 },
  wall: { inner: 3.95, thickness: 0.6 },
  pipe: { clearance: 1.3, diameter: 0.6 },
  pipeA: { z: 12, fromLane: 0, toLane: 2 },
  pipeB: { z: 28, fromLane: 1, toLane: 2 },
  rows: { D1: 5, D2: 20, D3: 36 },
  lamps: { spacing: 5, height: 3.3 },
  /** Banner on the entry portal, facing the runner (in front of the portal face). */
  portalBanner: { width: 3.6, height: 1.2, y: 5.05, inset: 0.06 },
  /** Hazard stripe on the portal header, right above the opening (the banner hangs above it). */
  portalStripe: 0.18,
  // offset from the wall face: 6 cm (was 3) – still behind the fire pipe (back face x 3.85), no z-fighting
  wallBanner: { y: 2.3, z: [10, 30], offset: 0.06 },
} as const;

const ROW_TYPES = [ObstacleType.Jump, ObstacleType.LaneChange] as const;
const FACE_RIGHT = 90 * DEG_TO_RAD;
const FACE_LEFT = -90 * DEG_TO_RAD;

/**
 * Low pipe hanging from the roof across lanes `fromLane`…`toLane` (up to the wall on the outer side). Solid up
 * to its crown – the hangers (Ø 5 cm) stand between the running lines.
 */
function lowPipe(name: string, spec: { readonly z: number; readonly fromLane: number; readonly toLane: number }): CeilingZone {
  const area = laneArea(WORLD_CONFIG, spec.fromLane, spec.toLane, spec.z - TUNNEL.pipe.diameter / 2, spec.z + TUNNEL.pipe.diameter / 2);
  const last = WORLD_CONFIG.laneCount - 1;
  return {
    id: `CEIL_${name}`,
    ...area,
    xFrom: spec.fromLane === 0 ? -TUNNEL.wall.inner : area.xFrom,
    xTo: spec.toLane === last ? TUNNEL.wall.inner : area.xTo,
    clearance: TUNNEL.pipe.clearance,
    top: TUNNEL.pipe.clearance + TUNNEL.pipe.diameter,
  };
}

const wallBanners: AdvertisingSlotDefinition[] = TUNNEL.wallBanner.z.flatMap((z, i) => {
  const s = i === 0 ? '' : `_${i + 1}`;
  const x = TUNNEL.wall.inner - TUNNEL.wallBanner.offset;
  return [
    { id: `AD_FENCE_LEFT${s}`, slotType: AdSlotType.FenceLeft, position: { x: -x, y: TUNNEL.wallBanner.y, z }, yaw: FACE_RIGHT, width: 6, height: 1.5 },
    { id: `AD_FENCE_RIGHT${s}`, slotType: AdSlotType.FenceRight, position: { x, y: TUNNEL.wallBanner.y, z }, yaw: FACE_LEFT, width: 6, height: 1.5 },
  ];
});

const at = (row: keyof typeof TUNNEL.rows, lane: number, obstacleId: string): FixedObstaclePlacement => ({ spawnPointId: `SOCKET_OBS_${row}_L${lane}`, obstacleId });
const STACK = 'OBS_MATERIAL_STACK_A';
const DRUM = 'OBS_CABLE_DRUM_A';

const STACK_CENTER: ObstacleVariant = { id: 'STACK_CENTER', weight: 1, placements: [at('D1', 1, STACK), at('D2', 0, DRUM), at('D2', 2, DRUM), at('D3', 1, DRUM)] };
const STACK_RIGHT: ObstacleVariant = {
  id: 'STACK_RIGHT',
  weight: 1,
  placements: [at('D1', 0, DRUM), at('D1', 2, STACK), at('D2', 1, DRUM), at('D3', 0, DRUM), at('D3', 2, DRUM)],
};
const DRUMS_LEFT: ObstacleVariant = { id: 'DRUMS_LEFT', weight: 1, placements: [at('D1', 0, DRUM), at('D1', 1, DRUM), at('D2', 2, DRUM), at('D3', 0, DRUM), at('D3', 1, DRUM)] };
const STACK_THEN_PAIR: ObstacleVariant = { id: 'STACK_THEN_PAIR', weight: 1, placements: [at('D1', 2, STACK), at('D2', 0, DRUM), at('D2', 1, DRUM), at('D3', 1, DRUM), at('D3', 2, DRUM)] };
const mirror = (id: string, variant: ObstacleVariant): ObstacleVariant => ({ id, weight: variant.weight, placements: mirroredPlacements(WORLD_CONFIG, variant.placements) });

const TUNNEL_LAYOUTS: ObstacleVariant[] = [
  STACK_CENTER,
  STACK_RIGHT,
  mirror('STACK_LEFT', STACK_RIGHT),
  DRUMS_LEFT,
  mirror('DRUMS_RIGHT', DRUMS_LEFT),
  STACK_THEN_PAIR,
];

/** Only the far backdrop (buildings, cranes) – everything nearer is inside the berm. */
export const BACKDROP_ONLY = (sockets: readonly DecorationSocket[]): DecorationSocket[] => sockets.filter((s) => s.tags.includes('backdrop'));

export const SEG_TUNNEL_40M: SegmentDefinition = {
  id: 'SEG_TUNNEL_40M',
  kind: SegmentKind.Straight,
  displayName: 'Keller / Tunnel 40 m',
  length: TUNNEL.length,
  width: WORLD_CONFIG.trackWidth,
  visualAssetId: 'SEG_TUNNEL_40M',
  extraVisuals: [
    { assetId: 'SEG_STRAIGHT_20M', position: { x: 0, y: 0, z: 0 } },
    { assetId: 'SEG_STRAIGHT_20M', position: { x: 0, y: 0, z: 20 } },
  ],
  enclosure: { kind: EnclosureKind.Tunnel, zFrom: 0, zTo: TUNNEL.length },

  entry: entryConnector(),
  exits: [straightExit(TUNNEL.length)],
  lanes: standardLanes(WORLD_CONFIG),

  terrain: {
    surfaces: [],
    gaps: [],
    ceilings: [
      {
        id: 'CEIL_TUNNEL_ROOF',
        zFrom: 0,
        zTo: TUNNEL.length,
        xFrom: -TUNNEL.wall.inner,
        xTo: TUNNEL.wall.inner,
        clearance: TUNNEL.roof.clearance,
        soft: true,
      },
      lowPipe('PIPE_A', TUNNEL.pipeA),
      lowPipe('PIPE_B', TUNNEL.pipeB),
    ],
  },

  obstacleSpawnPoints: [
    ...obstacleRow(WORLD_CONFIG, 'D1', TUNNEL.rows.D1, ROW_TYPES),
    ...obstacleRow(WORLD_CONFIG, 'D2', TUNNEL.rows.D2, ROW_TYPES),
    ...obstacleRow(WORLD_CONFIG, 'D3', TUNNEL.rows.D3, ROW_TYPES),
  ],
  obstacleRowLayouts: { sparse: [], dense: [] },
  // Six layouts (user 2026-10-03: "in den Tunneln … gleich positioniert – Abwechslung"): a lane change only in D1,
  // D2 / D3 jumps only (8 m behind the pipes).
  obstacleVariants: TUNNEL_LAYOUTS,

  collectibleSpawnPoints: collectibleLanes(WORLD_CONFIG, 1, TUNNEL.length - 1, 2, COLLECTIBLE_FLOAT_HEIGHT),

  decorationSockets: BACKDROP_ONLY(moduleSides(TUNNEL.length).decorationSockets),
  dynamicEventSockets: [],
  advertisingSlots: [
    {
      id: 'AD_TUNNEL_PORTAL',
      slotType: AdSlotType.ScaffoldHigh,
      // in front of the entry portal (z < 0), facing the approaching runner
      position: { x: 0, y: TUNNEL.portalBanner.y, z: -TUNNEL.portalBanner.inset },
      yaw: Math.PI,
      width: TUNNEL.portalBanner.width,
      height: TUNNEL.portalBanner.height,
      fillRate: 1,
    },
    ...wallBanners,
  ],

  generation: {
    weight: 0.12,
    minDistance: 250,
    cooldownDistance: 300,
    minSegmentsAfterJunction: 1,
    family: 'ENCLOSED',
    tags: ['tunnel', 'enclosed', 'terrain', 'slide'],
  },
};
